import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { INCIDENT_DETAILS } from '@elementary/common-ui/constants/queryKeys';
import useEnvironments from 'shared/hooks/useEnvironments';
import { SourceType } from 'shared/types';
import { EtlTool } from '@elementary/common-ui/services/gql/types';
import { getIncidentDetailsQuery } from '../api';
import {
  GraphQLSeverity,
  GraphQLStatus,
  IncidentDetailsResponse,
  IncidentOverviewData,
  Severity,
  Status,
} from '../../../types';

const mapGraphQLStatusToStatus = (graphQLStatus: GraphQLStatus): Status => {
  const statusMap: Record<GraphQLStatus, Status> = {
    OPEN: 'Open',
    ACKNOWLEDGED: 'Acknowledged',
    RESOLVED: 'Resolved',
  };
  return statusMap[graphQLStatus];
};

const mapGraphQLSeverityToSeverity = (
  graphQLSeverity: GraphQLSeverity,
): Severity => {
  const severityMap: Record<GraphQLSeverity, Severity> = {
    LOW: 'Low',
    NORMAL: 'Normal',
    HIGH: 'High',
    CRITICAL: 'Critical',
  };
  return severityMap[graphQLSeverity];
};

const transformIncidentData = (
  data: IncidentDetailsResponse,
): IncidentOverviewData => {
  const isPipelineIncident =
    data.source?.__typename === 'PipelineIncidentSource';
  const isTestIncident =
    !isPipelineIncident &&
    (data.source?.__typename === 'TestIncidentSource' || !!data.source?.test);
  const asset = data.source?.asset;
  const test = data.source?.test;
  const testExecution = data.source?.testExecution;
  const columnName = testExecution?.columnName;

  const fullyQualifiedName = asset
    ? [asset.dbName, asset.schemaName, asset.name, columnName]
        .filter(Boolean)
        .join('.')
    : undefined;

  const sourceType = isPipelineIncident
    ? SourceType.PIPELINE
    : isTestIncident
    ? SourceType.TEST
    : SourceType.MODEL;

  const sourceId = isPipelineIncident
    ? data.source?.connectorId || ''
    : isTestIncident
    ? testExecution?.subTestUniqueId || testExecution?.id || test?.id || ''
    : asset?.id || '';

  const pipelineData =
    isPipelineIncident && data.source
      ? {
          connectorName: data.source.connectorName ?? '',
          connectorType: data.source.connectorType ?? '',
          destination: data.source.destination ?? undefined,
          toolType: data.source.toolType ?? ('FIVETRAN' as EtlTool),
          connectorId: data.source.connectorId ?? '',
          rootEventId: data.source.rootEventId ?? '',
        }
      : undefined;

  return {
    ...data,
    status: mapGraphQLStatusToStatus(data.status),
    severity: mapGraphQLSeverityToSeverity(data.severity),
    sourceType,
    sourceId,
    tableAssetId: asset?.id,
    fullyQualifiedName,
    testName: test?.name,
    testType: test?.type,
    testSubType: testExecution?.subType,
    testDescription: test?.description,
    modelDescription: !isTestIncident ? asset?.description : undefined,
    isColumn: !!columnName,
    pipelineData,
  };
};

const useIncidentDetails = (incidentId?: string) => {
  const { selectedEnvId } = useEnvironments();

  const { data, isLoading, isFetching, error } = useGraphqlQuery<{
    incident: IncidentDetailsResponse;
  }>({
    queryKey: [INCIDENT_DETAILS, incidentId, selectedEnvId],
    query: getIncidentDetailsQuery,
    variables: {
      id: incidentId,
    },
    enabled: !!incidentId,
  });

  return {
    isLoading,
    isFetching,
    data: data?.incident ? transformIncidentData(data.incident) : undefined,
    error,
  };
};

export default useIncidentDetails;
